import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  certificationKeys,
  competencyKeys,
  organizationKeys,
  personKeys,
  roleKeys,
  skillKeys,
} from "@/lib/query";
import type {
  PersonCreateInput,
  PersonSelfContactInput,
  PersonUpdateInput,
} from "@/schemas/persons";

export interface Person {
  id: string;
  name: string;
  title: string | null;
  email: string | null;
  personalEmail: string | null;
  phone: string | null;
  entityName: string | null;
  employmentType: string;
  status: string | null;
  location: "us" | "intl" | null;
  /** A `role_classification.key` token — the set is admin-managed config. */
  roleClassification: string | null;
  hoursPerWeek: number | null;
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  workingHoursStart2: string | null;
  workingHoursEnd2: string | null;
  workingHoursNote: string | null;
  startDate: string | null;
  rehireDate: string | null;
  vcStartDate: string | null;
  /** The role team this person LEADS, not the one they belong to. */
  employeeProfileId: string | null;
  employeeProfileName: string | null;
  ledRoleTeamId: string | null;
  ledRoleTeamName: string | null;
  endDate: string | null;
  notes: string | null;
  userId: string | null;
  departmentId: string | null;
  roleTeamId: string | null;
  managerId: string | null;
  dottedLineId: string | null;
  avatarFileId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersonRoleTeam {
  id: string;
  name: string;
}

// Directory-list row: a person plus the department name and their role team
// (from the direct `role_team_id` FK, server-enriched).
export interface PersonListRow extends Person {
  departmentName: string | null;
  roleTeams: PersonRoleTeam[];
  /** Presigned profile-picture URL, or null when none is set. */
  avatarUrl: string | null;
}

export interface PersonDetail extends Person {
  userName: string | null;
  userEmail: string | null;
  departmentName: string | null;
  roleTeamName: string | null;
  managerName: string | null;
  dottedLineName: string | null;
  /** Presigned profile-picture URL, or null when none is set. */
  avatarUrl: string | null;
  /** Only populated by /api/me/person: does anyone report to this person? */
  isManager?: boolean;
  /** Raw fact behind the nav rule — see `showsTeamNav`. */
  hasDirectReports?: boolean;
}

/** A scheduled, not-yet-effective change to a single person field. */
export interface PersonPendingChange {
  id: string;
  field: string;
  label: string;
  effectiveDate: string;
  oldValue: unknown;
  oldValueLabel: string;
  newValue: unknown;
  newValueLabel: string;
  createdAt: string;
  actorName: string | null;
}

/** One field's before→after, resolved to display labels (confirm dialog). */
export interface PersonChangePreview {
  field: string;
  label: string;
  before: unknown;
  beforeLabel: string;
  after: unknown;
  afterLabel: string;
}

export interface PersonCertificationRow {
  id: string;
  personId: string;
  certificationId: string;
  certificationName: string;
  certificationIssuingBody: string | null;
  status: "assigned" | "started" | "completed";
  proofFileId: string | null;
  proofFilename: string | null;
  proofUploadedAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  assignedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersonRoleAssignmentRow {
  id: string;
  roleId: string;
  roleName: string;
  roleStatus: string;
  isPrimary: boolean;
  /** Career level held in this role; null when the role uses no levels. */
  roleLevelId: string | null;
  roleLevelName: string | null;
  startDate: string | null;
  endDate: string | null;
}

interface PersonsListResponse {
  data: PersonListRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export interface PersonFilters {
  search: string;
  statuses: string[];
  employmentTypes: string[];
  locations: string[];
  roleClassifications: string[];
  /** Multi-select org-placement + capability filters (empty = no filter). */
  departmentIds: string[];
  roleTeamIds: string[];
  skillIds: string[];
  /** Data-quality filter: only people whose manager is inactive (WI-076). */
  managerInactive?: boolean;
  sort?: string;
  dir?: "asc" | "desc";
}

function personFilterParams(filters: PersonFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.statuses.length) params.set("status", filters.statuses.join(","));
  if (filters.employmentTypes.length)
    params.set("employmentType", filters.employmentTypes.join(","));
  if (filters.locations.length) params.set("location", filters.locations.join(","));
  if (filters.roleClassifications.length)
    params.set("roleClassification", filters.roleClassifications.join(","));
  if (filters.departmentIds.length) params.set("departmentId", filters.departmentIds.join(","));
  if (filters.roleTeamIds.length) params.set("roleTeamId", filters.roleTeamIds.join(","));
  if (filters.skillIds.length) params.set("skillId", filters.skillIds.join(","));
  if (filters.managerInactive) params.set("managerInactive", "1");
  if (filters.sort) {
    params.set("sort", filters.sort);
    params.set("dir", filters.dir ?? "asc");
  }
  return params;
}

export function usePersons(filters: PersonFilters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...personKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<PersonsListResponse> => {
      const params = personFilterParams(filters);
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }

      const res = await fetch(`/api/persons?${params}`);
      if (!res.ok) throw new Error("Failed to fetch people");
      return res.json();
    },
  });
}

/**
 * Download the directory as CSV for the current search + filters. Admin-only
 * server-side; the button is hidden for non-admins. Streams the response to a
 * blob and triggers a browser download using the server-supplied filename.
 */
export function useExportPersons() {
  return useMutation({
    mutationFn: async (filters: PersonFilters) => {
      const res = await fetch(`/api/persons/export?${personFilterParams(filters)}`);
      if (!res.ok) throw new Error("Export failed");

      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? "person-directory.csv";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success("Directory exported"),
    onError: () => toast.error("Failed to export directory"),
  });
}

export function useMyPerson() {
  return useQuery({
    queryKey: personKeys.mine,
    queryFn: async (): Promise<PersonDetail | null> => {
      const res = await fetch("/api/me/person");
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch your person record");
      const json = await res.json();
      return json.data;
    },
  });
}

export function usePerson(personId: string) {
  return useQuery({
    queryKey: personKeys.detail(personId),
    queryFn: async (): Promise<PersonDetail | null> => {
      const res = await fetch(`/api/persons/${personId}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch person");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: PersonCreateInput) => {
      const res = await fetch("/api/persons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      // New person appears on the org chart (all active people) and, once
      // assigned, in role-team groupings.
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success("Person created");
    },
    onError: () => {
      toast.error("Failed to create person");
    },
  });
}

/** Effective-dated edit: the change is scheduled, not applied immediately. */
export function useUpdatePerson(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // `effectiveDate` omitted ⇒ the change applies immediately.
    // `cancelPendingChangeIds` withdraws overlapping scheduled changes in the
    // same save ("overwrite" choice in the confirm dialog).
    mutationFn: async (input: {
      effectiveDate?: string;
      updates: PersonUpdateInput;
      cancelPendingChangeIds?: string[];
      /** Why the change was made — required by the dialog when an org-placement field changed. */
      changeReasonId?: string | null;
    }) => {
      const res = await fetch(`/api/persons/${personId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        // Surface server-side validation (e.g. the reporting-cycle guard).
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.pending(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success(input.effectiveDate ? "Change scheduled" : "Changes saved");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Failed to save changes");
    },
  });
}

/**
 * Deactivate a person and reassign every direct report in a single atomic
 * request (WI-076). The server moves all reports (same effective date) and
 * applies the person's own edit in one transaction, so a failure rolls the
 * whole batch back — no half-moved reports. `updates` is the person's own edit
 * (status → inactive + any other changed fields); `reassignments` gives each
 * report its new manager (`null` = no manager).
 */
export function useDeactivatePerson(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // `effectiveDate` omitted ⇒ the deactivation + moves apply immediately.
    mutationFn: async (input: {
      effectiveDate?: string;
      updates: PersonUpdateInput;
      reassignments: { personId: string; managerId: string | null }[];
      cancelPendingChangeIds?: string[];
      changeReasonId?: string | null;
    }) => {
      const res = await fetch(`/api/persons/${personId}/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Deactivation failed");
      }
      return res.json();
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.pending(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success(input.effectiveDate ? "Change scheduled" : "Changes saved");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Failed to deactivate");
    },
  });
}

/**
 * Self-service contact edit — a user updating their OWN contact fields
 * (personal email, phone, location, working hours). Applied immediately; the
 * server notifies HR. Invalidates the person caches so every surface refreshes.
 */
export function useUpdateOwnContact(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PersonSelfContactInput) => {
      const res = await fetch("/api/me/person/contact", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success("Contact info updated");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to update contact info"),
  });
}

/**
 * Submit a free-text "request update" from the signed-in user's own profile
 * (e.g. asking someone to change their manager). Emailed to the configured
 * update-request recipients; returns how many it reached (0 = none configured).
 */
export function useRequestUpdate() {
  return useMutation({
    mutationFn: async (message: string): Promise<{ sent: number }> => {
      const res = await fetch("/api/me/person/request-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to send request");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: (data) => {
      if (data.sent > 0) toast.success("Update request sent");
      else toast.warning("Request saved, but no update-request recipient is configured yet");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to send request"),
  });
}

/** Withdraw a scheduled change before it takes effect (admin-only route). */
export function useCancelPendingChange(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changeId: string) => {
      const res = await fetch(`/api/persons/${personId}/pending-changes/${changeId}/cancel`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Cancel failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.pending(personId) });
      toast.success("Scheduled change canceled");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Failed to cancel scheduled change");
    },
  });
}

/**
 * Set a person's profile picture immediately (not effective-dated). Takes a
 * fileId from the /api/files presign→confirm upload flow. Invalidates the
 * directory, this person's detail, the org tree, and the "my person" record so
 * every surface that shows the avatar refreshes.
 */
export function useSetPersonAvatar(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/persons/${personId}/avatar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to update photo");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      toast.success("Profile picture updated");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Failed to update photo");
    },
  });
}

/** Remove a person's profile picture (and delete the backing file). */
export function useRemovePersonAvatar(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/persons/${personId}/avatar`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to remove photo");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      toast.success("Profile picture removed");
    },
    onError: () => {
      toast.error("Failed to remove photo");
    },
  });
}

/** Pending (not-yet-effective) changes for a person — drives the profile icons. */
export interface ManagerHistorySpan {
  id: string;
  /** The manager for this span; null is an explicit "nobody". */
  value: string | null;
  valueName: string | null;
  startDate: string;
  endDate: string | null;
  note: string | null;
  changeReasonId: string | null;
  changeReasonName: string | null;
  nosHistoryId: string | null;
}

/**
 * Manager spans for the profile's history panel. `enabled` so the fetch only
 * happens when the dialog is opened, not on every profile render — the route is
 * admin-or-self and most viewers will never open it.
 */
export type HistoryAttribute = "manager" | "dotted_line" | "role_classification";

export function usePersonHistory(personId: string, attribute: HistoryAttribute, enabled: boolean) {
  return useQuery({
    queryKey: personKeys.history(personId, attribute),
    enabled,
    queryFn: async (): Promise<ManagerHistorySpan[]> => {
      const res = await fetch(`/api/persons/${personId}/history/${attribute}`);
      if (!res.ok) throw new Error("Failed to fetch history");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface ManagerSpanInput {
  /** A person id for manager / dotted line; a classification token otherwise. */
  value: string | null;
  startDate: string;
  endDate: string | null;
  note?: string | null;
}

/**
 * Add / edit / delete a manager span. One factory rather than three near-identical
 * hooks; each invalidates both the history and the person detail, since editing
 * the OPEN span changes who the profile shows as the current manager.
 */
function useSpanMutation<TInput>(
  personId: string,
  attribute: HistoryAttribute,
  request: (input: TInput) => { url: string; method: "POST" | "PUT" | "DELETE"; body?: unknown },
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const { url, method, body } = request(input);
      const res = await fetch(url, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        // The service's messages are written for the admin reading the dialog
        // ("only one span can be open at a time"), so prefer them.
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Something went wrong");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.history(personId, attribute) });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      toast.success(successMessage);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAddSpan(personId: string, attribute: HistoryAttribute) {
  return useSpanMutation<ManagerSpanInput>(
    personId,
    attribute,
    (body) => ({ url: `/api/persons/${personId}/history/${attribute}`, method: "POST", body }),
    "Span added",
  );
}

export function useUpdateSpan(personId: string, attribute: HistoryAttribute) {
  return useSpanMutation<{ spanId: string; input: ManagerSpanInput }>(
    personId,
    attribute,
    ({ spanId, input }) => ({
      url: `/api/persons/${personId}/history/${attribute}/${spanId}`,
      method: "PUT",
      body: input,
    }),
    "Span updated",
  );
}

export function useDeleteSpan(personId: string, attribute: HistoryAttribute) {
  return useSpanMutation<{ spanId: string }>(
    personId,
    attribute,
    ({ spanId }) => ({
      url: `/api/persons/${personId}/history/${attribute}/${spanId}`,
      method: "DELETE",
    }),
    "Span deleted",
  );
}

/**
 * A person's scheduled changes — admins and that person only, server-side.
 *
 * `retry: false` because a 403 is a permanent answer for this viewer, not a
 * blip: every call site defaults to `[]`, so a forbidden read renders no
 * scheduled-change icons rather than an error. Same posture as
 * `usePersonAspirations`.
 */
export function usePersonPendingChanges(personId: string) {
  return useQuery({
    queryKey: personKeys.pending(personId),
    retry: false,
    queryFn: async (): Promise<PersonPendingChange[]> => {
      const res = await fetch(`/api/persons/${personId}/pending-changes`);
      if (!res.ok) throw new Error("Failed to fetch pending changes");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Dry-run an edit to get the field-level diff for the confirm dialog. A query
 * (not a mutation) so it fetches automatically when the dialog opens — gated by
 * `enabled` so it only runs while open with a payload to preview.
 */
export function usePersonChangePreview(
  personId: string,
  updates: PersonUpdateInput | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...personKeys.detail(personId), "preview", updates] as const,
    enabled: enabled && updates !== null,
    queryFn: async (): Promise<PersonChangePreview[]> => {
      const res = await fetch(`/api/persons/${personId}/preview-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to preview change");
      const json = await res.json();
      return json.data;
    },
  });
}

export function usePersonCertifications(personId: string) {
  return useQuery({
    queryKey: [...personKeys.detail(personId), "certifications"] as const,
    queryFn: async (): Promise<PersonCertificationRow[]> => {
      const res = await fetch(`/api/persons/${personId}/certifications`);
      if (!res.ok) throw new Error("Failed to fetch certifications");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useAssignCertification(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      certificationId: string;
      status?: "assigned" | "started" | "completed";
      expiresAt?: string | null;
      notes?: string | null;
    }) => {
      const res = await fetch(`/api/persons/${personId}/certifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Assign failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      // Refresh the certification's People tab (membership + status).
      queryClient.invalidateQueries({ queryKey: certificationKeys.all });
      toast.success("Certification assigned");
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to assign certification"),
  });
}

/**
 * Assign ONE certification to many people at once. People who already hold it
 * are skipped server-side; the response carries `{ assigned, skipped }` and the
 * toast reports both. Invalidates the whole persons + certifications caches
 * since the affected rows span many people.
 */
export function useBulkAssignCertifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      personIds: string[];
      certificationId: string;
      status?: "assigned" | "started" | "completed";
      expiresAt?: string | null;
      notes?: string | null;
    }): Promise<{ assigned: number; skipped: number }> => {
      const res = await fetch("/api/person-certifications/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Bulk assign failed");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: ({ assigned, skipped }) => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: certificationKeys.all });
      const noun = assigned === 1 ? "person" : "people";
      const parts = [`Assigned to ${assigned} ${noun}`];
      if (skipped > 0) parts.push(`${skipped} already had it`);
      toast.success(parts.join(" · "));
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to assign certification"),
  });
}

export function useUpdatePersonCertification(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      status?: "assigned" | "started" | "completed";
      proofFileId?: string | null;
      expiresAt?: string | null;
      notes?: string | null;
    }) => {
      const res = await fetch(`/api/person-certifications/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      // Refresh the certification's People tab (status/proof/expiry shown there).
      queryClient.invalidateQueries({ queryKey: certificationKeys.all });
      toast.success("Certification updated");
    },
    onError: () => toast.error("Failed to update certification"),
  });
}

export function useDeletePersonCertification(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/person-certifications/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      // Refresh the certification's People tab (membership).
      queryClient.invalidateQueries({ queryKey: certificationKeys.all });
      toast.success("Certification removed");
    },
    onError: () => toast.error("Failed to remove certification"),
  });
}

export function usePersonRoles(personId: string | null) {
  return useQuery({
    queryKey: [...personKeys.detail(personId ?? "none"), "roles"] as const,
    enabled: personId !== null,
    queryFn: async (): Promise<PersonRoleAssignmentRow[]> => {
      const res = await fetch(`/api/persons/${personId}/roles`);
      if (!res.ok) throw new Error("Failed to fetch person roles");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface DirectReport {
  id: string;
  name: string;
  title: string | null;
  /** Presigned profile-picture URL, or null when none is set. */
  avatarUrl: string | null;
}

export function usePersonDirectReports(personId: string) {
  return useQuery({
    queryKey: personKeys.directReports(personId),
    queryFn: async (): Promise<DirectReport[]> => {
      const res = await fetch(`/api/persons/${personId}/direct-reports`);
      if (!res.ok) throw new Error("Failed to fetch direct reports");
      const json = await res.json();
      return json.data;
    },
  });
}

/** People this person is a dotted-line mentor for (their mentees). */
export function usePersonDottedLineReports(personId: string) {
  return useQuery({
    queryKey: personKeys.dottedLineReports(personId),
    queryFn: async (): Promise<DirectReport[]> => {
      const res = await fetch(`/api/persons/${personId}/dotted-line-reports`);
      if (!res.ok) throw new Error("Failed to fetch dotted-line reports");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSetPersonRoles(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      effectiveDate: string;
      assignments: {
        roleId: string;
        isPrimary: boolean;
        roleLevelId: string | null;
        startDate: string | null;
        endDate: string | null;
      }[];
    }) => {
      const res = await fetch(`/api/persons/${personId}/roles`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      // A person's role set feeds the role "Employees" lists, the org tree /
      // chart (people grouped by role team), their own self-service views, and
      // the skill/competency People tabs (person → role → skill/competency).
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.mine });
      // A future-dated change shows as a scheduled/pending change on the profile.
      queryClient.invalidateQueries({ queryKey: personKeys.pending(personId) });
      toast.success("Roles updated");
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to update roles"),
  });
}
